import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { FaCar, FaCheck, FaMapMarkerAlt, FaPlus, FaSatelliteDish } from 'react-icons/fa';

const COLORS = ['#F56753', '#FB7185', '#22D3EE', '#F59E0B'];

const AddFundsModal = ({ isOpen, onClose, onSubmit, loading }) => {
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const numAmount = Number(amount);
    if (!numAmount || numAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (numAmount < 100) {
      setError('Minimum amount is ₹100');
      return;
    }
    setError('');
    onSubmit(numAmount);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-xl font-bold mb-4">Add Funds</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount (₹)
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F56753]"
              min="100"
              step="1"
            />
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-[#F56753] text-white rounded-md hover:bg-[#d94e3a] disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Add Funds'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CorporateAdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalBookings: 0,
    totalSpend: 0,
    totalSavings: 0,
    creditLimit: 0,
    addedFunds: 0,
    creditUsed: 0,
    totalAvailable: 0,
  });
  const [showAddFundsModal, setShowAddFundsModal] = useState(false);
  const [addFundsLoading, setAddFundsLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [timeFilter, setTimeFilter] = useState('quarter');
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [carTypeData, setCarTypeData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [customerData, setCustomerData] = useState([]);
  const [member, setMember] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionLimit] = useState(20);
  const [showTransactions, setShowTransactions] = useState(false);

  useEffect(() => {
    const corpToken = localStorage.getItem('corporateToken');
    if (!corpToken) {
      navigate('/corporate/login');
      return;
    }
    fetchDashboardData();
  }, [timeFilter]);

  const getHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('corporateToken')}`,
  });

  // ─── Date range helper ───────────────────────────────────────────────────
  const getDateRange = (filter) => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const d = now.getDate();
    const dow = now.getDay(); // 0 = Sunday

    let from, to;

    switch (filter) {
      case 'week': {
        from = new Date(y, m, d - dow);          // this Sunday
        to   = new Date(y, m, d - dow + 6, 23, 59, 59, 999); // this Saturday
        break;
      }
      case 'month': {
        from = new Date(y, m, 1);
        to   = new Date(y, m + 1, 0, 23, 59, 59, 999);
        break;
      }
      case 'quarter': {
        const qs = Math.floor(m / 3) * 3;
        from = new Date(y, qs, 1);
        to   = new Date(y, qs + 3, 0, 23, 59, 59, 999);
        break;
      }
      case 'year': {
        from = new Date(y, 0, 1);
        to   = new Date(y, 11, 31, 23, 59, 59, 999);
        break;
      }
      case 'last_week': {
        const lastSun = new Date(y, m, d - dow - 7);
        const lastSat = new Date(y, m, d - dow - 1, 23, 59, 59, 999);
        from = lastSun; to = lastSat;
        break;
      }
      case 'last_month': {
        from = new Date(y, m - 1, 1);
        to   = new Date(y, m, 0, 23, 59, 59, 999);
        break;
      }
      case 'last_quarter': {
        const cqs = Math.floor(m / 3) * 3;
        from = new Date(y, cqs - 3, 1);
        to   = new Date(y, cqs, 0, 23, 59, 59, 999);
        break;
      }
      default: {
        from = new Date(y, Math.floor(m / 3) * 3, 1);
        to   = new Date(y, Math.floor(m / 3) * 3 + 3, 0, 23, 59, 59, 999);
      }
    }
    return { from, to };
  };

  const isInRange = (booking, from, to) => {
    const date = new Date(booking.pickupDate || booking.createdAt);
    return date >= from && date <= to;
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const { from, to } = getDateRange(timeFilter);
      const fromISO = from.toISOString();
      const toISO   = to.toISOString();

      // Fetch member profile
      const memberRes = await axios.get('/api/corporate/me', { headers: getHeaders() });
      setMember(memberRes.data.data);

      // Fetch wallet balance (always global — not time-filtered)
      const walletRes = await axios.get('/api/corporate/wallet', { headers: getHeaders() });
      const walletData = walletRes.data.data || {};

      // Fetch ALL bookings (we filter on frontend for charts + stats)
      const bookingsRes = await axios.get('/api/corporate/bookings', { headers: getHeaders() });
      const allBookings = bookingsRes.data.data || [];
      setBookings(allBookings);

      // ── Filter bookings to selected time range ──
      const filteredBookings = allBookings.filter(b => isInRange(b, from, to));

      // ── Calculate period-specific stats from filtered bookings ──
      const periodSpend = filteredBookings
        .filter(b => b.status === 'completed' || b.status === 'confirmed' || b.corporateBillingStatus === 'deducted')
        .reduce((sum, b) => sum + (b.totalAmount || b.fare || 0), 0);

      const periodCreditUsed = filteredBookings
        .filter(b => b.corporateBillingStatus === 'deducted')
        .reduce((sum, b) => sum + (b.corporateBilledAmount || b.totalAmount || 0), 0);

      console.log(`[${timeFilter}] From: ${from.toDateString()} To: ${to.toDateString()}`);
      console.log(`[${timeFilter}] Filtered bookings: ${filteredBookings.length}`);
      console.log(`[${timeFilter}] Period spend: ${periodSpend}, Credit used: ${periodCreditUsed}`);

      setStats({
        totalBookings: filteredBookings.length,
        totalSpend: periodSpend,
        totalSavings: 0,
        creditLimit: walletData.creditLimit || 0,
        addedFunds: walletData.addedFunds || 0,
        creditUsed: (walletData.creditUsed || 0) + (walletData.reservedFunds || 0),
        totalAvailable: walletData.totalAvailable || 0,
        periodCreditUsed,
      });

      // Fetch pending approvals (always show pending regardless of time filter)
      const pendingRes = await axios.get('/api/corporate/bookings?status=pending_corporate_approval', {
        headers: getHeaders(),
      });
      setPendingApprovals(pendingRes.data.data?.slice(0, 5) || []);

      // Process chart data using filtered bookings
      processChartData(filteredBookings, from, to);

      // Fetch wallet transactions
      await fetchTransactions();
    } catch (error) {
      console.error('Dashboard data fetch error:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (page = 1) => {
    try {
      const res = await axios.get(`/api/corporate/wallet/transactions?page=${page}&limit=${transactionLimit}`, {
        headers: getHeaders(),
      });
      console.log('💰 Transactions API response:', res.data);
      // listCorporateTransactions returns array directly in data field
      const txData = Array.isArray(res.data.data)
        ? res.data.data
        : (res.data.data?.transactions || []);
      setTransactions(txData);
      setTransactionPage(page);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  };

  const processChartData = (bookings, from, to) => {
    // Car type distribution
    const carTypes = {};
    bookings.forEach(b => {
      const type = b.cabType?.name || b.vehicleType || 'Sedan';
      carTypes[type] = (carTypes[type] || 0) + 1;
    });
    const carData = Object.keys(carTypes).map(type => ({
      name: type,
      value: carTypes[type],
    }));
    setCarTypeData(carData);

    // Monthly bookings — show months relevant to the date range
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthly = months.map(month => ({ name: month, value: 0 }));
    bookings.forEach(b => {
      const date = new Date(b.pickupDate || b.createdAt || b.date);
      const monthIndex = date.getMonth();
      if (!isNaN(monthIndex)) {
        monthly[monthIndex].value += 1;
      }
    });

    // Show only months in the selected range
    const fromMonth = from ? from.getMonth() : 0;
    const toMonth   = to   ? to.getMonth()   : 11;
    const rangeMonths = fromMonth <= toMonth
      ? monthly.slice(fromMonth, toMonth + 1)
      : monthly; // fallback
    setMonthlyData(rangeMonths.length > 0 ? rangeMonths : monthly.slice(0, 6));

    // Customer bookings
    const customerMap = {};
    bookings.forEach(b => {
      const name = b.passengerDetails?.name || b.passengerName || b.employeeName || 'Unknown';
      customerMap[name] = (customerMap[name] || 0) + 1;
    });
    const customerChart = Object.keys(customerMap)
      .slice(0, 5)
      .map(name => ({ name, value: customerMap[name] }));
    setCustomerData(customerChart);
  };

  const handleAddFunds = async (amount) => {
    // Validate Razorpay key is configured
    const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY_ID;
    if (!razorpayKey || razorpayKey === 'rzp_test_key') {
      toast.error('Razorpay payment gateway not configured. Please contact support.');
      console.error('REACT_APP_RAZORPAY_KEY_ID not set in environment variables');
      setAddFundsLoading(false);
      setShowAddFundsModal(false);
      return;
    }

    setAddFundsLoading(true);
    try {
      // Create Razorpay order
      const orderRes = await axios.post('/api/corporate/wallet/add-funds-order', { amount }, { headers: getHeaders() });
      const { order } = orderRes.data.data;

      const options = {
        key: razorpayKey,
        amount: order.amount,
        currency: order.currency,
        name: 'CabZigo - Corporate',
        description: `Add Funds - ₹${amount}`,
        order_id: order.id,
        handler: async (response) => {
          try {
            // Verify payment
            await axios.post('/api/corporate/wallet/verify-add-funds', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }, { headers: getHeaders() });

            toast.success(`₹${amount} added to your corporate account successfully!`);
            setShowAddFundsModal(false);
            fetchDashboardData();
          } catch (verifyError) {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: member?.fullName || '',
          email: member?.email || '',
          contact: member?.mobileNumber || '',
        },
        theme: {
          color: '#F56753',
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response) {
        toast.error('Payment failed. Please try again.');
      });
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to initiate payment');
    } finally {
      setAddFundsLoading(false);
    }
  };

  const handleApprove = async (bookingId) => {
    try {
      await axios.post(`/api/corporate/bookings/${bookingId}/approve`, {}, {
        headers: getHeaders(),
      });
      toast.success('Booking approved!');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve booking');
    }
  };

  const handleReject = async (bookingId) => {
    try {
      await axios.post(`/api/corporate/bookings/${bookingId}/reject`, {}, {
        headers: getHeaders(),
      });
      toast.success('Booking rejected');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject booking');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return new Date(timeStr).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // ─── Display label helpers ───────────────────────────────────────────────
  const getWeekRange = () => {
    const now = new Date();
    const { from, to } = getDateRange('week');
    return {
      start: from.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
      end:   to.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
    };
  };

  const getLastWeekRange = () => {
    const { from, to } = getDateRange('last_week');
    return {
      start: from.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
      end:   to.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
    };
  };

  const getCurrentMonth = () =>
    new Date().toLocaleDateString('en-IN', { month: 'long' });

  const getLastMonth = () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toLocaleDateString('en-IN', { month: 'long' });
  };

  const getQuarterRange = () => {
    const { from, to } = getDateRange('quarter');
    return {
      start: from.toLocaleDateString('en-IN', { month: 'short' }),
      end:   to.toLocaleDateString('en-IN', { month: 'short' }),
    };
  };

  const getLastQuarterRange = () => {
    const { from, to } = getDateRange('last_quarter');
    return {
      start: from.toLocaleDateString('en-IN', { month: 'short' }),
      end:   to.toLocaleDateString('en-IN', { month: 'short' }),
    };
  };

  const getCurrentYear = () => new Date().getFullYear().toString();

  const getFilterLabel = () => {
    switch (timeFilter) {
      case 'week':         return `${getWeekRange().start} → ${getWeekRange().end}`;
      case 'month':        return getCurrentMonth();
      case 'quarter':      return `${getQuarterRange().start} → ${getQuarterRange().end}`;
      case 'year':         return getCurrentYear();
      case 'last_week':    return `${getLastWeekRange().start} → ${getLastWeekRange().end} (Last week)`;
      case 'last_month':   return `${getLastMonth()} (Last month)`;
      case 'last_quarter': return `${getLastQuarterRange().start} → ${getLastQuarterRange().end} (Last quarter)`;
      default:             return `${getQuarterRange().start} → ${getQuarterRange().end}`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
        {/* Dashboard Header with Time Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Dashboard <span className="text-sm font-normal text-gray-500">({getFilterLabel()})</span>
              </h2>
              {stats.totalBookings === 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  No Bookings Found for this period.
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setTimeFilter('week')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${timeFilter === 'week'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                <div className="text-xs text-gray-500">THIS WEEK</div>
                {getWeekRange().start} → {getWeekRange().end}
              </button>
              <button
                onClick={() => setTimeFilter('month')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${timeFilter === 'month'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                <div className="text-xs text-gray-500">THIS MONTH</div>
                {getCurrentMonth()}
              </button>
              <button
                onClick={() => setTimeFilter('quarter')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${timeFilter === 'quarter'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                <div className="text-xs text-gray-500">THIS QUARTER</div>
                {getQuarterRange().start} → {getQuarterRange().end}
              </button>
              <button
                onClick={() => setTimeFilter('year')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${timeFilter === 'year'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                <div className="text-xs text-gray-500">THIS YEAR</div>
                {getCurrentYear()}
              </button>
              {/* More Options Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setShowMoreOptions(true)}
                onMouseLeave={() => setShowMoreOptions(false)}
              >
                <button className={`px-4 py-4 rounded-lg text-sm font-medium transition-colors ${
                  ['last_week', 'last_month', 'last_quarter'].includes(timeFilter)
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}>
                  {timeFilter === 'last_week'    ? `Last week: ${getLastWeekRange().start}–${getLastWeekRange().end}`
                   : timeFilter === 'last_month'  ? `Last month: ${getLastMonth()}`
                   : timeFilter === 'last_quarter'? `Last quarter: ${getLastQuarterRange().start}–${getLastQuarterRange().end}`
                   : 'More Options'} <span className="ml-1">{showMoreOptions ? '▲' : '▼'}</span>
                </button>
                {showMoreOptions && (
                  <div className="absolute top-full left-0 mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                    <button
                      onClick={() => { setTimeFilter('last_week'); setShowMoreOptions(false); }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 first:rounded-t-lg ${
                        timeFilter === 'last_week' ? 'bg-gray-900 text-white' : 'text-gray-700'
                      }`}
                    >
                      <div className="text-xs opacity-60">LAST WEEK</div>
                      {getLastWeekRange().start} → {getLastWeekRange().end}
                    </button>
                    <button
                      onClick={() => { setTimeFilter('last_month'); setShowMoreOptions(false); }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                        timeFilter === 'last_month' ? 'bg-gray-900 text-white' : 'text-gray-700'
                      }`}
                    >
                      <div className="text-xs opacity-60">LAST MONTH</div>
                      {getLastMonth()}
                    </button>
                    <button
                      onClick={() => { setTimeFilter('last_quarter'); setShowMoreOptions(false); }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 last:rounded-b-lg ${
                        timeFilter === 'last_quarter' ? 'bg-gray-900 text-white' : 'text-gray-700'
                      }`}
                    >
                      <div className="text-xs opacity-60">LAST QUARTER</div>
                      {getLastQuarterRange().start} → {getLastQuarterRange().end}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {stats.totalBookings === 0 && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-amber-800 text-sm text-center">
                No bookings found for <strong>{getFilterLabel()}</strong>. Try a different time period.
              </p>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <p className="text-sm font-medium text-gray-600">Total Bookings</p>
            <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalBookings}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <p className="text-sm font-medium text-gray-600">Total Spend</p>
            <p className="text-3xl font-bold text-gray-900 mt-2">{formatCurrency(stats.totalSpend)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <p className="text-sm font-medium text-gray-600">Credit Used</p>
            <p className="text-3xl font-bold text-red-600 mt-2">{formatCurrency(stats.periodCreditUsed ?? stats.creditUsed)}</p>
            <p className="text-xs text-gray-400 mt-1">All-time: {formatCurrency(stats.creditUsed)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-gray-600">Available Balance</p>
                <p className="text-3xl font-bold text-green-600 mt-2">{formatCurrency(stats.totalAvailable)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Limit: {formatCurrency(stats.creditLimit)} + Funds: {formatCurrency(stats.addedFunds)}
                </p>
              </div>
              <button
                onClick={() => setShowAddFundsModal(true)}
                className="bg-[#F56753] hover:bg-[#d94e3a] text-white px-3 py-1 rounded text-sm font-medium"
              >
                + Add Funds
              </button>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Car Bookings Pie Chart */}
          <div className="bg-white rounded-lg shadow-sm p-3">
            <div className="bg-red-50 border border-red-200 rounded-b-lg py-2 text-center -mt-3">
              <h3 className="text-[#F56753] text-sm font-bold">
                Total Car Bookings
              </h3>
            </div>
            {carTypeData.length > 0 ? (
              <div className="h-56 relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={carTypeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={85}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {carTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, name]} />
                  </PieChart>
                </ResponsiveContainer>
                {/* Center Total */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-800">
                      {carTypeData.reduce((sum, item) => sum + item.value, 0)}
                    </p>
                    <p className="text-xs text-gray-500">Total</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <FaCar className="text-4xl mx-auto mb-2" style={{ color: 'rgb(254, 222, 173)' }} />
                  <p>No booking data available</p>
                </div>
              </div>
            )}
            <div className="flex justify-center gap-4 mt-3 text-xs flex-wrap px-2">
              {carTypeData.map((item, index) => (
                <span key={item.name} className="flex items-center gap-1.5">
                  <span
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  ></span>
                  <span className="text-gray-700 font-medium">
                    {item.name} — {item.value}
                  </span>
                </span>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-3">

            <div className="bg-red-50 border border-red-200 rounded-b-lg py-2 text-center -mt-3">
              <h3 className="text-[#F56753] text-sm font-bold">
                For You to Approve
              </h3>
            </div>

            <div className="flex justify-end pt-2 pr-1  mt-2 mb-4">
              <button
                onClick={() => navigate('/corporate/pending-approvals')}
                className="text-[#F56753] text-sm font-medium"
              >
                See All
              </button>
            </div>


            {pendingApprovals.length > 0 ? (
              <div className="relative">
                {/* Navigation Arrows */}
                {pendingApprovals.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentSlide(prev => prev === 0 ? pendingApprovals.length - 1 : prev - 1)}
                      className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 z-10 w-8 h-8 bg-white shadow-md rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100"
                    >
                      ←
                    </button>
                    <button
                      onClick={() => setCurrentSlide(prev => prev === pendingApprovals.length - 1 ? 0 : prev + 1)}
                      className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 z-10 w-8 h-8 bg-white shadow-md rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100"
                    >
                      →
                    </button>
                  </>
                )}

                {/* Carousel Container - Shows 1 card at a time */}
                <div className="overflow-hidden">
                  <div
                    className="flex transition-transform duration-300 ease-in-out"
                    style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                  >
                    {pendingApprovals.map((booking, index) => (
                      <div
                        key={booking._id}
                        className="w-full flex-shrink-0 border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-start gap-3">
                            <div className="flex flex-col items-center">
                              <img
                                src={booking.cabType?.imageUrl || 'https://via.placeholder.com/80'}
                                alt="Car"
                                className="w-20 h-14 rounded-lg object-cover"
                              />
                              <span className="px-2 py-0.5 bg-red-50 text-[#F56753] text-xs rounded mt-1">
                                {booking.fuelType || 'Petrol'}
                              </span>
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1.5">
                                <img
                                  src={booking.corporateMemberId?.profileImageUrl || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                                  alt="Passenger"
                                  className="w-7 h-7 rounded-full object-cover border border-gray-200 shadow-sm"
                                  onError={(e) => { e.target.src = "https://cdn-icons-png.flaticon.com/512/149/149071.png"; }}
                                />
                                <p className="font-semibold text-gray-900 text-base">
                                  {booking.passengerDetails?.name || booking.corporateMemberId?.fullName || 'Unknown'}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-gray-500">
                                  {booking.cabType?.name || 'Sedan'} | 4 Seats
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                {formatDate(booking.pickupDate)} | {booking.pickupTime}
                              </p>
                            </div>
                          </div>
                          <p className="text-2xl font-bold text-gray-900">{formatCurrency(booking.totalAmount || booking.fare)}</p>
                        </div>

                        <div className="flex items-start gap-3 mb-4 text-xs text-gray-600">
                          <img src="/icons/route-icon.png" alt="Route" className="w-5 h-12 object-contain mt-0.5" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{booking.from || booking.pickupLocation?.name || 'Pickup Location'}</p>
                            </div>
                            <p className="text-gray-400">{booking.pickupLocation?.address || booking.pickupLocation || ''}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <p className="font-medium">{booking.to || booking.dropLocation?.name || 'Drop Location'}</p>
                            </div>
                            <p className="text-gray-400">{booking.dropLocation?.address || booking.dropLocation || ''}</p>
                          </div>
                        </div>

                        <div className="flex gap-3">
                          <button
                            onClick={() => handleApprove(booking._id)}
                            className="flex-1 bg-[#F56753] hover:bg-[#d94e3a] text-white py-2.5 rounded-lg font-medium transition-colors"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(booking._id)}
                            className="flex-1 bg-red-50 hover:bg-red-100 text-[#F56753] py-2.5 rounded-lg font-medium transition-colors"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pagination Dots */}
                {pendingApprovals.length > 1 && (
                  <div className="flex justify-center gap-2 mt-4">
                    {pendingApprovals.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`w-2 h-2 rounded-full transition-colors ${index === currentSlide ? 'bg-[#F56753]' : 'bg-gray-300'
                          }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FaCheck className="text-4xl mx-auto mb-2" style={{ color: 'rgb(254, 222, 173)' }} />
                <p>No pending approvals</p>
              </div>
            )}
          </div>
        </div>

        {/* Bar Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Bookings Per Month */}
          <div className="bg-white rounded-lg shadow-sm p-3">
            <div className="bg-red-50 border border-red-200 rounded-b-lg py-2 text-center -mt-3">
              <h3 className="text-[#F56753] text-sm font-bold">
                Total Bookings Per Month
              </h3>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#F56753" radius={[4, 4, 0, 0]} barSize={35} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Customer Bookings */}
          <div className="bg-white rounded-lg shadow-sm p-3">
            <div className="bg-red-50 border border-red-200 rounded-b-lg py-2 text-center -mt-3">
              <h3 className="text-[#F56753] text-sm font-bold">
                Total Customer Bookings
              </h3>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={customerData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#F56753" radius={[4, 4, 0, 0]} barSize={35} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* ── Live Tracking Section ── */}
        {(() => {
          const activeBookings = bookings.filter(b =>
            ['assigned', 'inProgress', 'assigned_to_vendor'].includes(b.status)
          );
          if (activeBookings.length === 0) return null;
          return (
            <div className="mt-6 bg-white rounded-lg shadow-sm p-3">
              <div className="bg-green-100 border border-green-400 rounded-b-lg py-2 text-center -mt-3 mb-4">
                <h3 className="text-green-700 text-sm font-bold flex items-center justify-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                  Live Active Rides ({activeBookings.length})
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left">
                  <thead className="bg-gray-50 text-gray-700 font-medium">
                    <tr>
                      <th className="px-4 py-2">Booking ID</th>
                      <th className="px-4 py-2">Passenger</th>
                      <th className="px-4 py-2">From → To</th>
                      <th className="px-4 py-2">Date</th>
                      <th className="px-4 py-2">Amount</th>
                      <th className="px-4 py-2">Status</th>
                      <th className="px-4 py-2">Track</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {activeBookings.map((booking) => (
                      <tr key={booking._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono text-xs text-gray-600">{booking.bookingId}</td>
                        <td className="px-4 py-3 font-medium">
                          <div className="flex items-center gap-2">
                            <img
                              src={booking.corporateMemberId?.profileImageUrl || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                              alt="Passenger"
                              className="w-8 h-8 rounded-full object-cover border border-gray-200 shadow-sm"
                              onError={(e) => { e.target.src = "https://cdn-icons-png.flaticon.com/512/149/149071.png"; }}
                            />
                            <span>{booking.passengerDetails?.name || booking.corporateMemberId?.fullName || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          <span className="font-medium">{booking.from}</span>
                          <span className="mx-1 text-gray-400">→</span>
                          <span>{booking.to}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-500">{formatDate(booking.pickupDate)}</td>
                        <td className="px-4 py-3 font-medium">{formatCurrency(booking.totalAmount)}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            booking.status === 'inProgress'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {booking.status === 'inProgress' ? '🟢 In Progress' : '🔵 Assigned'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <a
                            href={`/track/${booking._id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 px-3 py-1.5 bg-[#F56753] hover:bg-[#d94e3a] text-white text-xs font-medium rounded-lg transition-colors"
                          >
                            <FaSatelliteDish className="text-xs" style={{ color: 'rgb(254, 222, 173)' }} />
                            Track Live
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* Wallet Transactions Section */}
        <div className="mt-6 bg-white rounded-lg shadow-sm p-3">
          <div className="bg-red-50 border border-red-200 rounded-b-lg py-2 text-center -mt-3 mb-4">
            <h3 className="text-[#F56753] text-sm font-bold">
              Wallet Transactions
            </h3>
          </div>
          <button
            onClick={() => setShowTransactions(!showTransactions)}
            className="mb-4 px-4 py-2 bg-[#F56753] text-white rounded-lg text-sm font-medium hover:bg-[#d94e3a]"
          >
            {showTransactions ? 'Hide Transactions' : 'View Transactions'}
          </button>

          {showTransactions && (
            <div className="overflow-x-auto">
              {transactions.length > 0 ? (
                <>
                  <table className="min-w-full text-sm text-left">
                    <thead className="bg-gray-50 text-gray-700 font-medium">
                      <tr>
                        <th className="px-4 py-2">Date</th>
                        <th className="px-4 py-2">Type</th>
                        <th className="px-4 py-2">Description</th>
                        <th className="px-4 py-2 text-right">Amount</th>
                        <th className="px-4 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {transactions.map((tx, index) => {
                        const isCredit = tx.isCredit || ['ADD_FUNDS', 'WALLET_TOPUP', 'RIDE_CREDIT', 'REFUND', 'credit', 'wallet_topup', 'add_funds'].includes(tx.type);
                        const typeLabel = tx.type === 'ADD_FUNDS' ? 'Add Funds'
                          : tx.type === 'WALLET_TOPUP' ? 'Wallet Topup'
                          : tx.type === 'RIDE_DEBIT' ? 'Ride Debit'
                          : tx.type === 'RIDE_CREDIT' ? 'Ride Credit'
                          : tx.type === 'REFUND' ? 'Refund'
                          : tx.type === 'wallet_topup' ? 'Add Funds'
                          : tx.type === 'booking' ? 'Booking'
                          : tx.type;
                        return (
                        <tr key={tx._id || tx.id || index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-600">
                            {new Date(tx.createdAt || tx.date).toLocaleDateString('en-IN')}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${isCredit ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {typeLabel}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-700">{tx.description || tx.note || '-'}</td>
                          <td className={`px-4 py-3 text-right font-medium ${isCredit ? 'text-green-600' : 'text-red-600'}`}>
                            {isCredit ? '+' : '-'}₹{Math.abs(tx.amount || 0).toLocaleString('en-IN')}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs ${tx.status === 'SUCCESS' || tx.status === 'completed' || tx.status === 'success'
                                ? 'bg-green-100 text-green-700'
                                : tx.status === 'PENDING' || tx.status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                              {tx.status || 'completed'}
                            </span>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="flex justify-between items-center mt-4 px-4">
                    <button
                      onClick={() => fetchTransactions(transactionPage - 1)}
                      disabled={transactionPage === 1}
                      className="px-3 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">Page {transactionPage}</span>
                    <button
                      onClick={() => fetchTransactions(transactionPage + 1)}
                      disabled={transactions.length < transactionLimit}
                      className="px-3 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-center text-gray-500 py-8">No transactions found</p>
              )}
            </div>
          )}
        </div>

      {/* Add Funds Modal */}
      <AddFundsModal
        isOpen={showAddFundsModal}
        onClose={() => setShowAddFundsModal(false)}
        onSubmit={handleAddFunds}
        loading={addFundsLoading}
      />
    </>
  );
};

export default CorporateAdminDashboard;
